/* eslint-disable react/require-default-props */
import React, { useState, useCallback } from 'react';
import Debug from 'debug';
import styled from '@emotion/styled';
import classNames from 'classnames';
import { withTheme } from '@emotion/react';
import { CardDetails } from './CardDetails';
import { CardCountDetails } from './CardCountDetails';
import { CardMenu, CardMenuProps } from './CardMenu';
import { toTimeAgo } from './timeAgo';
import { livefireItemTypes } from '@/constants';
import { ClickableElement } from '@/atoms';
import {
  ArrowSmall,
  LFOpen,
  LFSent,
  LFClick,
  LFPlay,
  ThumbsUpOutline,
  SpeechBubble,
  ReplyWithVideo,
} from '@/svgs';
import { book, colors, heavy, regular, semiBold } from '@/styles';
import { FeedItem } from '@/classes';
import { EventLivefireItemType } from '@/constants/LivefireItemTypes';
import { type HelpTooltipLinkDetails } from '@/atoms/HelpTooltip/HelpTooltip';

// eslint-disable-next-line new-cap
const debug = Debug('modulelibrary:livefirecard');

export type LivefireCardProps = {
  className?: string;
  id: string;
  title: string;
  subtitle: string | number;
  type: FeedItem['type'];
  recipient?: string | null;
  sent?: number;
  count?: number;
  percentWatched?: number;
  maxVideoWatchedPercent?: number;
  menuItems?: CardMenuProps['menuItems'];
  visibleMenuItems?: { arrow?: boolean; label: string; value: string }[];
  onMenuItemClick?: (selectedMenu: string) => void;
  onMenuOpen?: (open: boolean) => void;
  onMetricTriggered?: ((metric: string) => void) | null;
  onHelpTooltipVisible?: (status: boolean, position: number) => void;
  onHelpLinkClick?: ((action: HelpTooltipLinkDetails) => void) | null;
  position?: number;
  commentMessage?: string | null;
  replyThumbnail?: string | null;
  replyVideoName?: string | null;
  replyVideoUrl?: string | null;
};

/**
 * LivefireCard molecule for Livefire feed items
 */
export function LivefireCard({
  className = '',
  commentMessage = null,
  count = 0,
  id,
  menuItems = [],
  visibleMenuItems = [],
  onHelpLinkClick = null,
  onHelpTooltipVisible = () => {},
  onMenuItemClick = () => {},
  onMenuOpen = () => {},
  onMetricTriggered = null,
  percentWatched = -1,
  maxVideoWatchedPercent = -1,
  replyThumbnail = null,
  replyVideoName = null,
  replyVideoUrl = null,
  position = 0,
  recipient = null,
  sent = 0,
  subtitle,
  title,
  type,
}: LivefireCardProps) {
  const { EVENT_TYPES } = livefireItemTypes;
  const [expanded, setExpanded] = useState(false);
  const toggleExpanded = useCallback(() => setExpanded(!expanded), [expanded]);

  // do not show cards for event types we do not support (and do not include events)
  if (
    !livefireItemTypes.items.includes(type) ||
    EVENT_TYPES.includes(type as EventLivefireItemType)
  ) {
    return null;
  }

  let icon = null;
  switch (type) {
    case 'click':
      icon = <LFClick size={`${14.79 / 2.85}`} />;
      break;
    case 'reaction_comment':
      icon = <SpeechBubble fill={colors.taosSky} size={`${12 / 2.85}`} />;
      break;
    case 'reaction_like':
      icon = <ThumbsUpOutline fill={colors.taosSky} size={12 / 2.85} title="Like" />;
      break;
    case 'open':
      icon = <LFOpen size={`${12.18 / 2.85}`} />;
      break;
    case 'video_watched':
    case 'play':
      icon = <LFPlay size={`${14.74 / 2.85}`} />;
      break;
    case 'sent':
      icon = <LFSent size={`${11.06 / 2.85}`} />;
      break;
    case 'reaction_rwv':
      icon = <ReplyWithVideo size={`${9 / 2.85}`} />;
      break;
    default:
      break;
  }

  let tooltipVisibleTimeout: ReturnType<typeof setTimeout> | null = null;

  return (
    <article
      key={id}
      className={classNames(className, `card--${type}`, {
        commentMessage,
        expanded,
        [`card-position--${position}`]: position > 0,
        standalone: percentWatched > maxVideoWatchedPercent,
      })}
    >
      <div className={`icon icon--${type}`}>{icon}</div>
      <h1 className="title" title={title}>
        {type === livefireItemTypes.replyWithVideo ? `Video reply to: ${title}` : title}
      </h1>
      <h2 className="subtitle">{toTimeAgo(subtitle)}</h2>
      <CardDetails
        replyVideoUrl={replyVideoUrl}
        replyThumbnail={replyThumbnail}
        replyVideoName={replyVideoName}
        id={id}
        toggleExpanded={toggleExpanded}
        expanded={expanded}
        type={type}
        recipient={recipient}
        sent={sent}
        commentMessage={commentMessage}
        percentWatched={percentWatched}
        maxVideoWatchedPercent={maxVideoWatchedPercent}
        onMetricTriggered={onMetricTriggered}
        onTooltipVisible={(status) => {
          if (status === false && tooltipVisibleTimeout !== null) {
            clearTimeout(tooltipVisibleTimeout);
            tooltipVisibleTimeout = null;
          }

          if (status === true) {
            tooltipVisibleTimeout = setTimeout(() => {
              debug('tooltip visible metric fired');
              onMetricTriggered?.('show.livefire.help_tooltip');
            }, 500);
          }

          onHelpTooltipVisible(status, position);
        }}
        onHelpLinkClick={(action) => {
          debug('help link clicked', action);
          onHelpLinkClick?.(action);
          onMetricTriggered?.('click.livefire.help_tooltip_link');
        }}
      />
      <CardCountDetails type={type} count={count} />
      <CardMenu
        menuItems={menuItems}
        onMenuItemClick={(selectedMenu) => onMenuItemClick(selectedMenu)}
        onMenuOpen={onMenuOpen}
      />
      {!!visibleMenuItems.length && (
        <div className="visible-menu">
          {visibleMenuItems.map((item) => (
            <ClickableElement
              className="menu-item"
              key={item.value}
              onClick={() => onMenuItemClick(item.value)}
            >
              {item.label}
              {item.arrow && (
                <span className="arrow">
                  <ArrowSmall size={`${6.68 / 2.85}`} />
                </span>
              )}
            </ClickableElement>
          ))}
        </div>
      )}
    </article>
  );
}

const styledLivefireCard = styled(LivefireCard)`
  position: relative;
  ${({ theme }) => (theme.isRebrand ? regular() : book())}
  display: grid;
  ${({ visibleMenuItems = [], percentWatched = -1, maxVideoWatchedPercent = -1 }) => {
    const count = visibleMenuItems.length;

    // video watched items with no longest watch need different sizing
    const standalone = percentWatched > maxVideoWatchedPercent;

    return `
      min-height: ${111 / 16 + (count ? count + 22 / 16 : 0)}rem;
      grid-template-rows: [titleAndIconRow] 1em [subtitleRow] 1em [countAndRecipientRow] ${
        count ? `${standalone ? 60 / 16 : 72 / 16}rem [visibleMenuRow] 1fr` : '1fr'
      };
      &.card--reaction_like {
        min-height: unset;
        grid-template-rows:
[titleAndIconRow] 1em [subtitleRow] 1em [countAndRecipientRow] 2.5rem [visibleMenuRow] 1fr;
        grid-row-end: countColumn-end;
        .card-details {
          padding-right: 0;
          .recipient {
            word-break: break-word;
          }
        }
      };
      &.card--reaction_comment {
        min-height: unset;
        grid-template-rows:
        [titleAndIconRow] 1em [subtitleRow] 1em [countAndRecipientRow] 3.5rem [visibleMenuRow] 1fr;
          &.expanded {
            grid-template-rows:
  [titleAndIconRow] 1em [subtitleRow] 1em [countAndRecipientRow] max-content [visibleMenuRow] 1fr;
          .card-details .recipient {
            margin: 6px 0;
          }
        };
      };
      &.card--reaction_rwv {
        min-height: ${111 / 16 + (count ? count + 22 / 16 : 0)}rem;
        grid-template-rows:
          [titleAndIconRow] 1em [subtitleRow] 1em [countAndRecipientRow] 5rem [visibleMenuRow] 1fr;
        &.expanded {
          grid-template-rows:
[titleAndIconRow] 1em [subtitleRow] 1em [countAndRecipientRow] max-content [visibleMenuRow] 1fr;
          .card-details .recipient {
              margin: 0;
          }
        }

        > .icon.icon--reaction_rwv {
          margin-bottom: 2px;
        }

        .reply-with-video {
          display: flex;
          align-items: center;
          padding-right: ${35.57 / 16}rem;
          margin-bottom: 5px;

          .reply-details {
            margin-left: 14px;
            padding-right: ${35.57 / 16}rem;
            max-width: 100%;

            p {
              margin: 0;
            }

          }

          .thumbnail-container {
            width: 83.55px;
            height: 47px;
            background: black;
            text-align: center;
          }

          .video-thumbnail {
            height: 47px;
          }
        }

        &:not(.commentMessage) {
          grid-template-rows:
          [titleAndIconRow] 1em [subtitleRow] 1em [countAndRecipientRow] 4rem [visibleMenuRow] 1fr;
        }
      };
      padding: 1rem 0 ${count ? '0' : `${12.58 / 16}rem`};
    `;
  }}
  grid-template-columns: ${() => `
[iconColumn] ${35.57 / 16}rem [detailsColumn] minmax(0, 1fr) [countColumn] ${60.93 / 16}rem;
    `};
  margin-bottom: ${14 / 16}rem;
  background-color: ${colors.white};
  border-radius: ${2 / 16}rem;
  text-align: left;

  * {
    box-sizing: border-box;
  }

  > .icon {
    align-self: center;
    grid-column-start: iconColumn;
    grid-row-start: titleAndIconRow;
    margin-left: ${12 / 16}rem;
    margin-right: ${8.7 / 16}rem;
    padding-top: ${1 / 16}rem;
    width: ${15 / 16}rem;
  }

  > .icon.icon--click {
    padding-top: 0;
  }

  > .icon.icon--play,
  > .icon.icon--video_watched {
    padding-top: ${7 / 16}rem;
  }

  h1.title {
    grid-column-start: detailsColumn;
    grid-row-start: titleAndIconRow;
    ${({ theme }) => (theme.isRebrand ? semiBold() : heavy())}
    color: ${colors.boulder};
    font-size: ${14 / 16}rem;
    margin: 0 2em 0 0;
    padding: 0;
    height: 1.1rem;

    /* this is left here to trim on initial render b/c word safe trimming will happen after that */
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
  }

  h2.subtitle {
    grid-column-start: detailsColumn;
    grid-row-start: subtitleRow;
    ${({ theme }) => (theme.isRebrand ? semiBold() : heavy())}
    color: ${colors.silverSandAlt};
    font-size: ${12 / 16}rem;
    padding: 0;
    padding-top: ${3.82 / 16}rem;
    margin: 0;
  }

  .menu {
    grid-column-start: countColumn;
    grid-row-start: titleAndIconRow;

    > div {
      height: 1em;
    }

    > div > div[role='button'] {
      height: 1em;
    }

    .list {
      max-width: ${170 / 16}rem;
    }

    .dots.rotated {
      width: 100%;
      height: 1em;
      transform: none;

      svg {
        transform: rotate(90deg) translate(0, -100%);
        margin-left: 0.25rem;
      }
    }
  }

  .card-details {
    position: relative;
    grid-column-start: detailsColumn;
    grid-row-start: countAndRecipientRow;
    line-height: 1rem;
    align-self: end;
    padding-top: ${12 / 16}rem;
    margin-bottom: 0.05rem;

    ${({ theme }) => (theme.isRebrand ? regular() : book())}
    font-size: ${12 / 16}rem;
    color: ${colors.silverSandAlt};

    .recipient {
      padding-right: 1rem;
      margin-bottom: 0.1rem;
      overflow: hidden;
      ${(props) =>
        props.type !== 'reaction_like' &&
        `text-overflow: ellipsis;
      white-space: nowrap;`}

      /* overflow: hidden breaks showing the tooltip */
      &.someone {
        overflow: visible;
        white-space: normal;
      }

      strong {
        ${({ theme }) => (theme.isRebrand ? semiBold() : heavy())}

        &.blue {
          color: ${colors.taosSky};
        }
      }
    }

    .percent-watched {
      display: grid;
      grid-template-columns: 1fr 1fr;
      column-gap: ${22.34 / 16}rem;
      margin-top: ${8 / 16}rem;

      &.standalone {
        grid-template-columns: 1fr;
      }
    }

    .icon {
      position: relative;
      top: ${2 / 16}rem;
    }
  }

  &.card--video_watched,
  &.card--reaction_comment,
  &.card--reaction_like,
  &.card--reaction_rwv {
    .card-details {
      grid-column-end: countColumn-end;
      padding-right: ${35.57 / 16}rem;
      padding-top: ${7 / 16}rem;
      max-width: 100%;
    }
  }

  &.card--reaction_like .card-details {
    padding-right: 0;
  }

  &.card--reaction_comment .title {
    grid-column-end: visibleMenuRow;
  }

  .progress-bar-override {
    .progress-background {
      background-color: ${colors.whiteSmoke};
    }

    .text-over {
      color: ${colors.boulder};
      font-size: ${11 / 16}rem;
    }
  }

  .count-details {
    display: inline-grid;
    position: relative;
    top: -${5 / 16}rem;
    grid-column-start: countColumn;
    grid-row-start: countAndRecipientRow;
    height: ${27 / 16}rem;
    align-self: end;
    text-align: center;
    ${({ theme }) => (theme.isRebrand ? semiBold() : heavy())}
    color: ${colors.boulder};
    padding-left: ${10 / 16}rem;
    padding-right: ${13 / 16}rem;
    border-left: ${1 / 16}rem solid ${colors.whiteSmoke};

    .count {
      display: block;
      position: relative;
      top: -0.2em;
      font-size: 1rem;
      line-height: ${22 / 16}rem;
    }

    .countLabel {
      display: block;
      position: relative;
      top: -0.65em;
      font-size: ${11 / 16}rem;
      line-height: ${15 / 16}rem;
    }
  }

  &.card--video_watched .count-details {
    grid-row-start: subtitleRow;
  }

  &.card--reaction_like .count-details {
    grid-row-start: subtitleRow;
  }

  abbr[title] {
    border-bottom: none;
    text-decoration: none;
  }

  .visible-menu {
    grid-column: iconColumn / countColumn-end;
    grid-row: visibleMenuRow;
    border-top: ${1 / 16}rem solid ${colors.whiteSmoke};
    margin-top: ${11.5 / 16}rem;

    .menu-item {
      ${({ theme }) => (theme.isRebrand ? semiBold() : heavy())}
      color: ${colors.boulder};
      font-size: ${12 / 16}rem;
      text-align: center;
      padding: ${6 / 16}rem;
      cursor: pointer;
      transition: color 150ms cubic-bezier(0.39, 0.58, 0.57, 1);

      .arrow {
        display: inline-block;
        margin-left: ${4.85 / 16}rem;
      }

      svg * {
        fill: ${colors.boulder};
        transition: fill 150ms cubic-bezier(0.39, 0.58, 0.57, 1);
      }

      &:hover {
        color: ${colors.taosSky};

        svg * {
          fill: ${colors.taosSky};
        }
      }
    }
  }
`;

export default withTheme(styledLivefireCard);
